import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import useMap from '@alltrails/maps/hooks/useMap';
import { WAYPOINT_LAT_PARAM, WAYPOINT_LNG_PARAM, WAYPOINT_ZOOM } from '@alltrails/maps/utils/customRoutes/constants';

/**
 * Parses `?waypoint_lat=&waypoint_lng=` from the URL once per hook instance (snapshot at mount).
 */
export const useWaypointFocusLatLng = (): { lat: number; lng: number } | null => {
  const searchParams = useSearchParams();

  const [latLng] = useState<{ lat: number; lng: number } | null>(() => {
    const lat = searchParams.get(WAYPOINT_LAT_PARAM);
    const lng = searchParams.get(WAYPOINT_LNG_PARAM);
    if (!lat || !lng) {
      return null;
    }
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      return null;
    }
    return { lat: latNum, lng: lngNum };
  });

  return latLng;
};

/**
 * Flies to waypoint coords & strips waypoint params from the URL.
 */
export const useApplyWaypointFocus = (latLng: { lat: number; lng: number } | null): void => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const map = useMap();

  useEffect(() => {
    if (!latLng || !map) {
      return;
    }

    map.flyTo({ center: [latLng.lng, latLng.lat], zoom: WAYPOINT_ZOOM });

    const params = new URLSearchParams(searchParams.toString());
    params.delete(WAYPOINT_LAT_PARAM);
    params.delete(WAYPOINT_LNG_PARAM);
    const newUrl = `${pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    router.replace(newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run once on mount; subsequent searchParams changes (e.g. after the strip) shouldn't refire.
  }, []);
};
